import type {RouteObject} from '@github-ui/react-router'

/**
 * Metadata stamped onto each repo sub-app route's handle (see {@link withRepoRouteHandle}) so the
 * matched route can be mapped back to:
 *  - `repoSubAppName`: the standalone app it was split from, for per-sub-app telemetry
 *    (RepoLayout's StatsMetaTag → getCurrentReactAppName).
 *  - `selectedLink`: the `selected_link` identifier that highlights its local-nav tab
 *    (useSyncActiveNavTab, matched against each tab's `selectedLinks`, e.g. `repo_issues`).
 *
 * react-core's `RouteMatchHandle` only declares `queryRoute`, so consumers read these back via a cast.
 */
export interface RepoRouteHandle {
  repoSubAppName?: string
  selectedLink?: string
}

/**
 * Stamps `handle` onto every route in `routes` (recursively, preserving any existing handle keys), so
 * RepoLayout can recover the active sub-app + nav tab from the matched route — no hand-maintained
 * route→app table. Returns the array so it can be spread straight into the tree.
 *
 * Mutates in place, which is safe because the repo app rebuilds these trees per request (the route
 * factories return fresh objects on every call).
 */
export function withRepoRouteHandle(routes: RouteObject[], handle: RepoRouteHandle): RouteObject[] {
  for (const route of routes) {
    route.handle = {...(route.handle as object | undefined), ...handle}
    if (route.children) withRepoRouteHandle(route.children, handle)
  }
  return routes
}
